"use client";

import { useCallback } from "react";
import { X } from "lucide-react";
import type { FilterConfig } from "@/lib/types/data.types";
import type { FilterColumnDef } from "./types";
import {
  getDisplayLabel,
  getOperatorSymbol,
  getColumnDef,
  toPascalSpaced,
} from "./helpers";

interface ActiveFilterBadgesProps {
  columns: FilterColumnDef[];
  filters: FilterConfig[];
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onClearAll: () => void;
  /** For multi-select: remove a single value from a comma-joined filter. */
  onRemoveMultiSelectValue?: (filterIndex: number, value: string) => void;
}

export function ActiveFilterBadges({
  columns,
  filters,
  onRemove,
  onEdit,
  onClearAll,
  onRemoveMultiSelectValue,
}: ActiveFilterBadgesProps) {
  if (filters.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-900">
          Active Filters
        </span>
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-gray-500 hover:text-red-600 transition-colors"
        >
          Clear All
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {filters.flatMap((filter, index) => {
          const col = getColumnDef(columns, filter.column);
          const isMultiSelect = col?.type === "multi-select";

          if (isMultiSelect) {
            const values = String(filter.value)
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);

            return values.map((v) => (
              <FilterBadge
                key={`${index}-${v}`}
                columns={columns}
                filter={{ ...filter, value: v }}
                displayValue={
                  col?.options?.find((o) => o.value === v)?.label ??
                  toPascalSpaced(v)
                }
                onClick={() => onEdit(index)}
                onRemove={() =>
                  onRemoveMultiSelectValue
                    ? onRemoveMultiSelectValue(index, v)
                    : onRemove(index)
                }
              />
            ));
          }

          return (
            <FilterBadge
              key={index}
              columns={columns}
              filter={filter}
              onClick={() => onEdit(index)}
              onRemove={() => onRemove(index)}
            />
          );
        })}
      </div>
      <div className="border-t border-gray-100 mt-3" />
    </div>
  );
}

// ── Single badge ────────────────────────────────────────────────

interface FilterBadgeProps {
  columns: FilterColumnDef[];
  filter: FilterConfig;
  displayValue?: string;
  onClick: () => void;
  onRemove: () => void;
}

function FilterBadge({
  columns,
  filter,
  displayValue,
  onClick,
  onRemove,
}: FilterBadgeProps) {
  const display = getDisplayLabel(columns, filter);
  const symbol = getOperatorSymbol(filter.operator);

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove();
    },
    [onRemove]
  );

  return (
    <span
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
    >
      <span className="font-medium">{display.column}</span>
      <span className="text-gray-400">{symbol ?? display.operator}</span>
      <span className="font-semibold">
        {displayValue ?? display.value}
      </span>
      <button
        type="button"
        onClick={handleRemove}
        className="ml-0.5 text-gray-400 hover:text-red-500 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}
